import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  Back,
  Megaphone,
  // Next,
  Trash,
} from '../../../../assets/js/svg/gui';
import { useSingleCase } from '../../../../stores';
import useExplainAction from '../../../../stores/tutorial/useExplainAction';
import { Button } from '../../../buttons';
import { Column } from '../../../display';
import ConfirmPopup from '../../../display/ConfirmPopup';
import RadioButton from '../../../input/RadioButton';
import SvgColorImg from '../../../SvgColorImg';

function ChatMessageControls({
  translate,
  author,
  message,
  createAlert,
}) {
  const explainAction = useExplainAction();
  const { thisCase } = useSingleCase();
  const { blockUser, canBlockUser, crHandler, participants } = thisCase;

  const dotsRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const [showReasonPicker, setShowReasonPicker] = useState(false);
  const [selectedReason, setSelectedReason] = useState('conflictWithUser');

  const authorCrUid = message?.crUid;

  // Block can only target an end user, and only a handler can place it.
  const { canModerate, blockReasons } = useMemo(
    () => {
      const authorParticipant = (participants || [])
        .find((thisParticipant) => (thisParticipant.crUid === authorCrUid));

      const moderatable = (!!crHandler)
        && (!!authorParticipant)
        && (authorParticipant.gcatCode === 'users')
        && canBlockUser;

      // "repeated bad reports" only when the user is recognisable across cases.
      const canIdentify = authorParticipant
        && (((authorParticipant.member && authorParticipant.memberEmail))
          || ((authorParticipant.crFname && authorParticipant.crLname))
          || authorParticipant.phoneMob);

      return {
        canModerate: moderatable,
        blockReasons: [
          'conflictWithUser',
          'abusiveOrOffensiveBehaviour',
          ...((canIdentify) ? ['repeatedBadReports'] : []),
          'otherReason',
        ],
      };
    },
    [participants, authorCrUid, crHandler, canBlockUser],
  );

  // The menu is portalled to the body: the message row is overflow-hidden and
  // virtualized, so an in-place absolute popup would be clipped. Anchor it to
  // the dots button's screen position.
  const openMenu = useCallback(
    () => {
      const rect = dotsRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuPos({
          top: rect.bottom + 4,
          right: Math.max(8, globalThis.innerWidth - rect.right),
        });
      }
    },
    [],
  );

  const closeMenu = useCallback(() => setMenuPos(null), []);

  const openModerate = useCallback(
    () => {
      setMenuPos(null);
      setShowReasonPicker(true);
    },
    [],
  );

  const confirmBlock = useCallback(
    async () => {
      setShowReasonPicker(false);
      await blockUser({
        tgtCrUid: authorCrUid,
        block: true,
        reason: selectedReason,
        // The offending message is the audit reference for this block.
        answrToMsgId: message?.id,
      });
    },
    [blockUser, authorCrUid, selectedReason, message],
  );

  const handleAlert = useCallback(
    () => {
      setMenuPos(null);
      if (createAlert) createAlert({ author: author || '', ...message });
    },
    [createAlert, author, message],
  );

  // Only others' messages carry an author and can be moderated.
  if (!author) return <span />;

  const rowClassName = 'flex flex-row items-center justify-between gap-6 px-4 py-2 w-full text-start';
  const iconClassName = 'w-5 h-5 shrink-0 fill-pxPrimary';
  const disabledRow = 'opacity-40 cursor-not-allowed';

  return (
    <div ref={dotsRef}>
      <Button
        id="dots"
        onClick={explainAction('home.cases.messageMenu', openMenu)}
        imgSrc="/images/svg/gui/dotdotdot.svg"
        tiny
        noFrame
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out"
      />

      {(menuPos)
        ? createPortal(
            <>
              <button
                type="button"
                aria-label="close"
                className="fixed inset-0 z-2000 bg-transparent cursor-default"
                onClick={closeMenu}
              />
              <div
                className="fixed z-2000 min-w-44 rounded-2xl bg-white shadow-xl py-2 flex flex-col"
                style={{ top: menuPos.top, right: menuPos.right }}
              >
                <button type="button" disabled className={classNames(rowClassName, disabledRow)}>
                  <span>{translate('toAnswer')}</span>
                  <Back className={iconClassName} />
                </button>
                {/* <button type="button" disabled className={classNames(rowClassName, disabledRow)}>
                  <span>{translate('forward')}</span>
                  <Next className={iconClassName} />
                </button> */}
                <button type="button" disabled className={classNames(rowClassName, disabledRow)}>
                  <span>{translate('delete')}</span>
                  <Trash className={iconClassName} />
                </button>
                <button
                  type="button"
                  onClick={openModerate}
                  disabled={!canModerate}
                  className={classNames(
                    rowClassName,
                    'font-semibold',
                    (canModerate) ? 'hover:bg-pxMainBgnd2' : disabledRow,
                  )}
                >
                  <span>{translate('moderate')}</span>
                  <SvgColorImg
                    src="/images/svg/gui/moderate.svg"
                    imgColorAsCssFill
                    className="w-5 h-5 shrink-0 fill-pxPrimary"
                  />
                </button>
                {(createAlert)
                  ? (
                      <button
                        type="button"
                        onClick={handleAlert}
                        className={classNames(rowClassName, 'hover:bg-pxMainBgnd2')}
                      >
                        <span>{translate('createGlobalAlert')}</span>
                        <Megaphone className={iconClassName} />
                      </button>
                    )
                  : null}
              </div>
            </>,
            globalThis.document.body,
          )
        : null}

      {(showReasonPicker)
        ? createPortal(
            <ConfirmPopup
              confirmCritical
              title={translate('reasonForBlocking')}
              onConfirm={confirmBlock}
              onReject={() => setShowReasonPicker(false)}
            >
              <Column crossStart>
                {blockReasons.map((thisReason) => (
                  <RadioButton
                    key={thisReason}
                    id={thisReason}
                    name={thisReason}
                    label={translate(thisReason, 'questionnaireAnswers')}
                    selected={selectedReason === thisReason}
                    onChange={() => setSelectedReason(thisReason)}
                  />
                ))}
              </Column>
            </ConfirmPopup>,
            globalThis.document.body,
          )
        : null}
    </div>
  );
}

ChatMessageControls.propTypes = {
  translate: PropTypes.func.isRequired,
  author: PropTypes.string,
  createAlert: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number,
    crUid: PropTypes.number,
  }),
};

export default ChatMessageControls;
